/**
 * Quick chat at the tables (mahjong-table, doudizhu): fixed phrases and emoji reactions only — no free text at the
 * tables, no chat at the shared rounds (owner decision 2026-09-30; docs/superpowers/specs/2026-09-30-table-quick-chat-design.md).
 * The client sends an id; the texts live in apps/web/messages/<lang>/table-chat.json (`phrase.<id>`).
 */
import { z } from 'zod';
import type { ApiError } from './errors';

export const TABLE_CHAT_GAMES = ['mahjong-table', 'doudizhu', 'chess'] as const;
export type TableChatGame = (typeof TABLE_CHAT_GAMES)[number];

/** 你好 快点吧 打得好 谢谢 不好意思 再来一局 厉害 运气真好 等一下 加油 恭喜 再见 */
export const TABLE_CHAT_PHRASES = [
  'hello',
  'hurry',
  'wellPlayed',
  'thanks',
  'sorry',
  'again',
  'impressive',
  'lucky',
  'wait',
  'goodLuck',
  'congrats',
  'bye',
] as const;
export type TableChatPhrase = (typeof TABLE_CHAT_PHRASES)[number];

/** neutral reactions: id → symbol (no money, slots, skulls) */
export const TABLE_CHAT_EMOJIS = {
  thumbsUp: '👍',
  smile: '😄',
  wow: '😮',
  thanks: '🙏',
  clap: '👏',
  tea: '🍵',
  party: '🎉',
  handshake: '🤝',
} as const;
export type TableChatEmoji = keyof typeof TABLE_CHAT_EMOJIS;
export const TABLE_CHAT_EMOJI_IDS = Object.keys(TABLE_CHAT_EMOJIS) as TableChatEmoji[];

/** one message per player per 3 s … */
export const TABLE_CHAT_MIN_INTERVAL_MS = 3_000;
/** … and at most 10 a minute */
export const TABLE_CHAT_PER_MINUTE = 10;
/** a bubble stays this long at the seat */
export const TABLE_CHAT_BUBBLE_MS = 3_000;

export const zTableChatInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('phrase'), id: z.enum(TABLE_CHAT_PHRASES) }).strict(),
  z
    .object({
      kind: z.literal('emoji'),
      id: z.enum(TABLE_CHAT_EMOJI_IDS as [TableChatEmoji, ...TableChatEmoji[]]),
    })
    .strict(),
]);
export type TableChatInput = z.infer<typeof zTableChatInput>;

/** `table:chat` — to every human at the table (the sender too) */
export type TableChatMessage = TableChatInput & {
  game: TableChatGame;
  tableId: string;
  seat: number;
  /** server time, ms */
  at: number;
};

/** the send event of each table game */
export const TABLE_CHAT_EVENTS = {
  'mahjong-table': 'mjt:chat',
  doudizhu: 'ddz:chat',
  chess: 'chess:chat',
} as const satisfies Record<TableChatGame, string>;

type ChatAck = (r: { ok: true } | ApiError) => void;

export interface TableChatClientToServer {
  /** Quick phrase/reaction at the own live chess table. No spectators or training bots. */
  'chess:chat': (input: TableChatInput, ack: ChatAck) => void;
  /** a phrase / an emoji at the own mahjong table: UNAUTHORIZED, VALIDATION_FAILED, ROUND_NOT_ACTIVE (not seated), BANNED, RATE_LIMITED */
  'mjt:chat': (input: TableChatInput, ack: ChatAck) => void;
  /** the same at the own dou dizhu table */
  'ddz:chat': (input: TableChatInput, ack: ChatAck) => void;
}
export interface TableChatServerToClient {
  'table:chat': (m: TableChatMessage) => void;
}
